import { DEFAULT_OFFERING_INDUSTRIES, type ServiceConditionInput, type ServiceOfferingInput } from "./profileServicesPricingDisplay";
import { inferServicesPricingOfferingIconKey } from "./profileServicesPricingOfferingIcons";

export type ServiceConditionEditorEntry = Readonly<{
  id: string;
  conditionKey: string;
  title: string;
  description: string;
  conditionStatus: string;
  isNew?: boolean;
}>;

export type ServiceOfferingEditorEntry = Readonly<{
  id: string;
  serviceKey: string;
  title: string;
  description: string;
  serviceCategory: string;
  pricingModel: string;
  priceMinCents?: number;
  priceMaxCents?: number;
  currency?: string;
  turnaroundDaysMin?: number;
  turnaroundDaysMax?: number;
  serviceTagLabels: readonly string[];
  targetIndustryLabels: readonly string[];
  deliverables: readonly string[];
  serviceStatus: string;
  iconKey?: string;
  purchaseCount?: number;
  averageRating?: number;
  reviewCount?: number;
  featuredReviewerInitials?: readonly string[];
  isNew?: boolean;
}>;

export type ServiceConditionsEditorDraft = Readonly<{
  entries: readonly ServiceConditionEditorEntry[];
}>;

export type ServiceOfferingsEditorDraft = Readonly<{
  entries: readonly ServiceOfferingEditorEntry[];
}>;

export type ServiceConditionsEditorDiff = Readonly<{
  idsToArchive: readonly string[];
  entriesToUpsert: readonly ServiceConditionEditorEntry[];
}>;

export type ServiceOfferingsEditorDiff = Readonly<{
  idsToArchive: readonly string[];
  entriesToUpsert: readonly ServiceOfferingEditorEntry[];
}>;

function slugKey(value: string): string {
  return value.toLowerCase().replace(/[^a-z0-9]+/g, "_").replace(/^_|_$/g, "") || "entry";
}

export function seedServiceConditionsEditorDraft(
  records: readonly ServiceConditionInput[],
): ServiceConditionsEditorDraft {
  return {
    entries: records
      .filter((record) => record.conditionStatus === "active")
      .map((record) => ({
        id: record.id,
        conditionKey: record.conditionKey,
        title: record.conditionTitle,
        description: record.conditionDescription,
        conditionStatus: record.conditionStatus,
      })),
  };
}

export function seedServiceOfferingsEditorDraft(
  records: readonly ServiceOfferingInput[],
): ServiceOfferingsEditorDraft {
  return {
    entries: records
      .filter((record) => record.serviceStatus !== "archived" && record.serviceStatus !== "suppressed")
      .map((record) => ({
        id: record.id,
        serviceKey: record.serviceKey,
        title: record.serviceTitle,
        description: record.description ?? "",
        serviceCategory: record.serviceCategory,
        pricingModel: record.pricingModel,
        priceMinCents: record.priceMinCents,
        priceMaxCents: record.priceMaxCents,
        currency: record.currency,
        turnaroundDaysMin: record.turnaroundDaysMin,
        turnaroundDaysMax: record.turnaroundDaysMax,
        serviceTagLabels: [...(record.serviceTagLabels ?? [])],
        targetIndustryLabels: [...(record.targetIndustryLabels ?? [])],
        deliverables: [...record.deliverables],
        serviceStatus: record.serviceStatus,
        iconKey: record.iconKey ?? inferServicesPricingOfferingIconKey({
          serviceCategory: record.serviceCategory,
          serviceTitle: record.serviceTitle,
          serviceTagLabels: record.serviceTagLabels,
        }),
        ...(record.purchaseCount !== undefined ? { purchaseCount: record.purchaseCount } : {}),
        ...(record.averageRating !== undefined ? { averageRating: record.averageRating } : {}),
        ...(record.reviewCount !== undefined ? { reviewCount: record.reviewCount } : {}),
        ...(record.featuredReviewerInitials?.length ? { featuredReviewerInitials: [...record.featuredReviewerInitials] } : {}),
      })),
  };
}

export function diffServiceConditionsEditor(
  baseline: ServiceConditionsEditorDraft,
  draft: ServiceConditionsEditorDraft,
): ServiceConditionsEditorDiff {
  const draftIds = new Set(draft.entries.map((entry) => entry.id));
  const idsToArchive = baseline.entries
    .filter((entry) => !draftIds.has(entry.id))
    .map((entry) => entry.id);
  return {
    idsToArchive,
    entriesToUpsert: draft.entries,
  };
}

export function diffServiceOfferingsEditor(
  baseline: ServiceOfferingsEditorDraft,
  draft: ServiceOfferingsEditorDraft,
): ServiceOfferingsEditorDiff {
  const draftIds = new Set(draft.entries.map((entry) => entry.id));
  const idsToArchive = baseline.entries
    .filter((entry) => !draftIds.has(entry.id))
    .map((entry) => entry.id);
  return {
    idsToArchive,
    entriesToUpsert: draft.entries,
  };
}

export function createEmptyServiceConditionEntry(index: number): ServiceConditionEditorEntry {
  const title = "New Condition";
  return {
    id: `new-condition-${index}`,
    conditionKey: slugKey(`${title}-${index}`),
    title,
    description: "Add the service condition details here.",
    conditionStatus: "active",
    isNew: true,
  };
}

export function createEmptyServiceOfferingEntry(index: number): ServiceOfferingEditorEntry {
  const title = "New Offering";
  return {
    id: `new-offering-${index}`,
    serviceKey: slugKey(`${title}-${index}`),
    title,
    description: "Describe this offering.",
    serviceCategory: "other",
    pricingModel: "fixed",
    priceMinCents: 0,
    priceMaxCents: 0,
    currency: "USD",
    turnaroundDaysMin: 1,
    turnaroundDaysMax: 3,
    serviceTagLabels: ["Custom"],
    targetIndustryLabels: ["General"],
    deliverables: ["Add a deliverable"],
    serviceStatus: "listed",
    iconKey: inferServicesPricingOfferingIconKey({
      serviceCategory: "other",
      serviceTitle: title,
      serviceTagLabels: ["Custom"],
    }),
    purchaseCount: 0,
    averageRating: 0,
    reviewCount: 0,
    featuredReviewerInitials: [],
    isNew: true,
  };
}

export function parseTurnaroundRangeLabel(label: string): Readonly<{ min?: number; max?: number }> {
  const trimmed = label.trim();
  const rangeMatch = trimmed.match(/^(\d+)\s*-\s*(\d+)/);
  if (rangeMatch) {
    return { min: Number(rangeMatch[1]), max: Number(rangeMatch[2]) };
  }
  const singleMatch = trimmed.match(/^(\d+)/);
  if (singleMatch) {
    const value = Number(singleMatch[1]);
    return { min: value, max: value };
  }
  return {};
}

export function formatTurnaroundRangeLabel(min?: number, max?: number): string {
  if (min === undefined && max === undefined) return "";
  if (min !== undefined && max !== undefined) {
    return min === max ? `${min} days` : `${min}-${max} days`;
  }
  if (min !== undefined) return `${min} days`;
  return `${max} days`;
}

export const SERVICE_OFFERING_INDUSTRY_SUGGESTIONS = [
  "Construction",
  "Infrastructure",
  "Energy",
  "Real Estate",
  "Agriculture",
  "Media",
  "Insurance",
  "Government",
  "Security",
  "Events",
  "General",
] as const;

export function splitServiceOfferingFreeTextList(value: string): readonly string[] {
  return value
    .split(/\r?\n|,/)
    .map((item) => item.trim())
    .filter(Boolean);
}

export function collectServiceOfferingIndustrySuggestions(
  entries: readonly ServiceOfferingEditorEntry[],
): readonly string[] {
  return Array.from(new Set([
    ...SERVICE_OFFERING_INDUSTRY_SUGGESTIONS,
    ...entries.flatMap((entry) => entry.targetIndustryLabels),
  ]));
}

// Mirrors collectOfferingIndustryFilters (display side) but operates on editor
// entries, so the edit modal exposes the same industry filter as the read tile.
export function collectServiceOfferingEditorIndustryFilters(
  entries: readonly ServiceOfferingEditorEntry[],
  allLabel: string,
): readonly string[] {
  const industries = new Set<string>(DEFAULT_OFFERING_INDUSTRIES);
  for (const entry of entries) {
    for (const industry of entry.targetIndustryLabels) industries.add(industry);
  }
  return [allLabel, ...Array.from(industries).filter((entry) => entry !== allLabel)];
}

export function filterServiceOfferingEditorEntriesByIndustry(
  entries: readonly ServiceOfferingEditorEntry[],
  filter: string,
  allLabel: string,
): readonly ServiceOfferingEditorEntry[] {
  if (filter === allLabel) return entries;
  return entries.filter((entry) => entry.targetIndustryLabels.includes(filter));
}
